.panel {
  display: grid;
  gap: var(--space-5);
}

/*
 * The status card is the board's masthead: the date, the clock and what the day costs, on
 * the one elevated surface in the panel. Everything below it is a row on that board.
 */
.status {
  display: grid;
  gap: var(--space-3);
}

.closed {
  color: var(--text-muted);
  font-weight: 600;
}

/* Canon § 6: the cold-start explanation. Two short paragraphs rather than one, because the
   second is the reassurance — nothing is required from the founder — and it is the half a
   worried reader is looking for. */
.coldStart {
  color: var(--text-muted);
  max-inline-size: var(--measure);
}

.coldStart + .coldStart {
  margin-block-start: var(--space-3);
}

.terms,
.available {
  color: var(--text-muted);
  max-inline-size: var(--measure);
}

/*
 * Canon § 12's sponsored disclosure. Brass and rule-marked so it reads as board furniture
 * that is always present, never as a warning that appeared because something is wrong.
 */
.disclosure {
  padding-block-start: var(--space-3);
  border-block-start: 1px solid var(--border);
  color: var(--text-muted);
  font-size: var(--text-sm);
  max-inline-size: var(--measure);
}

.deals {
  display: grid;
  gap: var(--space-4);
  /* Not read as prose, so it clears its own marker: `base.css` zeroes list margin and
   * padding for every repo but deliberately leaves `list-style` alone (`APP-018`).
   * `role="list"` on the element keeps the semantics Safari drops with the marker. */
  list-style: none;
}

.deal {
  display: grid;
  gap: var(--space-4);
}

.head {
  display: flex;
  align-items: flex-start;
  gap: var(--space-4);
}

/*
 * The rank mark. Brass, in the figure face, at a fixed width so a column of positions lines
 * up and a two-digit rank does not shunt the product name sideways. Decorative: the position
 * is always written out beside it, because canon forbids status by mark or colour alone.
 */
.mark {
  flex: none;
  min-inline-size: 3rem;
  font-family: var(--font-figure);
  font-size: var(--text-xl);
  font-weight: 600;
  color: var(--brass);
  font-variant-numeric: tabular-nums;
}

.names {
  display: grid;
  gap: var(--space-1);
  min-inline-size: 0;
}

.product {
  font-size: var(--text-lg);
}

.standing {
  color: var(--text-muted);
  font-size: var(--text-sm);
}

/*
 * Winning and losing differ in words first. The border and the hue are a second signal on
 * top of a sentence that already says which it is (contracts/DESIGN-TOKENS.md).
 */
.winning,
.losing {
  padding-inline-start: var(--space-3);
  border-inline-start: 3px solid var(--border-strong);
  font-size: var(--text-sm);
  max-inline-size: var(--measure);
}

.winning {
  border-inline-start-color: var(--positive);
}

.losing {
  border-inline-start-color: var(--brass-rule);
  color: var(--text-muted);
}

.shut {
  color: var(--text-muted);
  font-size: var(--text-sm);
  max-inline-size: var(--measure);
}

.form {
  display: grid;
  gap: var(--space-3);
  padding-block-start: var(--space-4);
  border-block-start: 1px solid var(--border);
}

/*
 * Mobile-first: the amount and the button stack, both full width and comfortably tappable.
 * From 30rem the button sits beside the field and aligns to its bottom edge, so the hint
 * text below the input does not drag the button down with it.
 */
.controls {
  display: grid;
  gap: var(--space-3);
}

@media (min-width: 30rem) {
  .controls {
    grid-template-columns: minmax(0, 14rem) auto;
    align-items: start;
  }

  .submit {
    /* Aligns with the input rather than the label above it. */
    margin-block-start: 1.6rem;
  }
}

.accepted {
  display: grid;
  gap: var(--space-2);
  padding: var(--space-4);
  border: 1px solid var(--border);
  border-inline-start: 3px solid var(--positive);
  border-radius: var(--radius-md);
  background: var(--surface);
  font-size: var(--text-sm);
  color: var(--text-muted);
  max-inline-size: var(--measure);
}

.acceptedTitle {
  color: var(--text);
  font-weight: 600;
  font-size: var(--text-base);
}

.acceptedCredit {
  padding-block-start: var(--space-2);
  border-block-start: 1px solid var(--border);
}

.failure {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  padding: var(--space-4);
  border: 1px solid var(--border);
  border-inline-start: 3px solid var(--danger);
  border-radius: var(--radius-md);
  background: var(--surface);
  color: var(--danger);
  font-size: var(--text-sm);
  max-inline-size: var(--measure);
}
